import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import styles from './DogSelectionModal.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAllDogs } from '../../../hooks/useBreedingDogsQueries';

interface Dog {
    [key: string]: any;
}

interface DogSelectionModalProps {
    show: boolean;
    onHide: () => void;
    dogs: Dog[]; // This prop is no longer used, but kept for backward compatibility
    onSelectDog: (dog: Dog) => void;
    selectedBreed: string;
    selectDog: boolean;
    setSelectDog: (select: boolean) => void;
    existingDogs?: Dog[];
    existingTotal?: number;
    existingPageSize?: number;
    hasSearched?: boolean;
    onSearchStatusChange?: (hasSearched: boolean) => void;
}

function DogSelectionModal({
    show,
    onHide,
    dogs,
    onSelectDog,
    selectedBreed,
    selectDog,
    setSelectDog,
    existingDogs,
    existingTotal,
    existingPageSize,
    hasSearched,
    onSearchStatusChange
}: DogSelectionModalProps) {
    const t = useTranslate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedDog, setSelectedDog] = useState<Dog | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const [sorting, setSorting] = useState<{ field: string; order: 'asc' | 'desc' } | null>(null);
    
    // Handle search on Enter key press
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearchStatusChange?.(true);
            setDebouncedSearchTerm(searchTerm);
            setPage(0); // Reset to first page when searching
        }
    };
    
    // Reset to first page when filters change
    useEffect(() => {
        setPage(0);
    }, [debouncedSearchTerm, sorting]);
    
    // Convert search term to API filter format
    // Use the same format as PageTable: { field, type, values, exact }
    // For LIKE searches, exact: false will use %INPUT% pattern
    const apiFilters = useMemo(() => {
        const filters: any[] = [];
        
        if (debouncedSearchTerm != "" && debouncedSearchTerm && debouncedSearchTerm.trim()) {
            // Add filter for NAME field - using PageTable format
            filters.push({
                field: 'NAME',
                type: 'or', // Use 'or' to search NAME or REGNO
                values: [debouncedSearchTerm.trim()],
                exact: false // false means LIKE %INPUT%
            });
            
            // Add filter for REGNO field - same search term
            filters.push({
                field: 'REGNO',
                type: 'or', // Use 'or' to combine with NAME filter
                values: [debouncedSearchTerm.trim()],
                exact: false // false means LIKE %INPUT%
            });
        }
        
        return filters;
    }, [debouncedSearchTerm]);
    
    // Determine if we can use existing data (no search, no sorting, same page size, and on first page)
    const canUseExistingData = useMemo(() => {
        return !debouncedSearchTerm && !sorting && 
               existingDogs && existingDogs.length > 0 && 
               existingPageSize === pageSize &&
               page === 0; // Only use existing data on first page
    }, [debouncedSearchTerm, sorting, existingDogs, existingPageSize, pageSize, page]);
    
    // Fetch dogs with server-side pagination, filtering, and sorting
    // Only fetch when modal is shown, breed is selected, and we can't use existing data
    const skip = page * pageSize;
    const shouldFetch = show && !!selectedBreed && !!selectedBreed.trim() && !canUseExistingData;
    
    const { data: dogsResponse, isLoading: dogsLoading, error: dogsError } = useAllDogs(
        shouldFetch ? selectedBreed : '', // Pass empty string to disable query
        apiFilters,
        sorting,
        pageSize,
        skip
    );
    
    // Use existing data if available and no search/filter/sort and on first page, otherwise use fetched data
    const filteredDogs = useMemo(() => {
        if (canUseExistingData && existingDogs && page === 0) {
            // Use existing data directly (already paginated from parent)
            return existingDogs;
        }
        return dogsResponse?.dogs || [];
    }, [canUseExistingData, existingDogs, page, dogsResponse?.dogs]);
    
    const totalDogs = useMemo(() => {
        if (canUseExistingData && existingTotal !== undefined && page === 0) {
            return existingTotal;
        }
        return dogsResponse?.total || 0;
    }, [canUseExistingData, existingTotal, page, dogsResponse?.total]);

    const handleSelectDog = (dog: Dog) => {
        setSelectedDog(dog);
    };

    const handleConfirmSelection = () => {
        if (selectedDog) {
            onSelectDog(selectedDog);
            setSelectedDog(null);
            setSearchTerm('');
            onHide();
        }
    };

    const handleClose = () => {
        setSelectedDog(null);
        setSearchTerm('');
        setPage(0);
        setSorting(null);
        onHide();
        setSelectDog(false);
        onSearchStatusChange?.(false);
    };
    
    const handleSort = (field: string) => {
        setSorting(prev => {
            if (prev && prev.field === field) {
                return {
                    field,
                    order: prev.order === 'asc' ? 'desc' : 'asc'
                };
            }
            return { field, order: 'asc' };
        });
        setPage(0);
    };

    const totalPages = Math.ceil(totalDogs / pageSize);

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton>
                {selectDog && 
                    <Modal.Title
                        onClick={() => {
                            setSelectDog(false);
                            setSearchTerm('');
                            onSearchStatusChange?.(false);
                        }}
                        style={{ cursor: 'pointer' }}
                    >{t("externalportal.breeding.modal.backtoselect")}</Modal.Title>
                }
            </Modal.Header>
            <Modal.Body>
                { !selectDog && (
                    <>
                        <p className={styles.selectdogselectionstext}>{t("externalportal.breeding.modal.selectdogtext")}</p>
                        <div className={styles.selectdogselections}>
                            <div
                                className={styles.selection + " " + styles.selectdog}
                                onClick={() => {
                                    setSelectDog(true);
                                }}
                            >
                                <div className={styles.optionIcon}>
                                    <i className="fas fa-paw"></i>
                                </div>
                                <p>{t("externalportal.breeding.modal.selections.selectdog")}</p>
                            </div>
                            <div
                                className={styles.selection + " " + styles.searchall}
                                onClick={() => {
                                    handleClose();
                                }}
                            >
                                <div className={styles.optionIcon}>
                                    <i className="fas fa-chart-bar"></i>
                                </div>
                                <p>{t("externalportal.breeding.modal.selections.searchall")}</p>
                            </div>
                        </div>
                    </>
                )}
                {selectDog && (
                    <>
                        <p>{t("externalportal.breeding.modal.description")}</p>
                        <div className={styles.modalContent}>
                            <div className={styles.searchSection}>
                                <Form.Control
                                    type="text"
                                    placeholder="Søk etter navn, reg.nr..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    className={styles.searchInput}
                                />
                                <button 
                                    className={styles.velgHundButton}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        background: '#4CAF50',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '13px',
                                        color: '#FFFFFF',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s ease',
                                        width: 'fit-content',
                                        minWidth: 'auto'
                                    }}
                                    onClick={() => {
                                        onSearchStatusChange?.(true);
                                        setDebouncedSearchTerm(searchTerm);
                                        setPage(0); // Reset to first page when searching
                                    }}
                                >
                                    {t("externalportal.breeding.modal.searchbtn")}
                                </button>
                            </div>
                            
                            {dogsError ? (
                                <div className={styles.noResults}>
                                    <p>Feil ved henting av hunder. Prøv igjen.</p>
                                </div>
                            ) : dogsLoading ? (
                                <div className={styles.noResults}>
                                    <p>Henter hunder...</p>
                                </div>
                            ) : filteredDogs.length > 0 && hasSearched ? (
                                <>
                                    <div className={styles.dogsList}>
                                        {filteredDogs.map((dog: Dog, index: number) => (
                                            <div
                                                key={`${dog.REGNO}-${dog.NAME}-${index}`}
                                                className={`${styles.dogItem} ${selectedDog?.REGNO === dog.REGNO ? styles.selected : ''}`}
                                                onClick={() => handleSelectDog(dog)}
                                            >
                                                <div className={styles.dogInfo}>
                                                    <div className={styles.dogName}>
                                                        <strong>{dog.NAME || 'Ukjent navn'}</strong>
                                                        <span className={styles.regNo}>{dog.REGNO || 'Ukjent reg.nr'}</span>
                                                    </div>
                                                    <div className={styles.dogDetails}>
                                                        <div className={styles.dogDetail}>
                                                            <span className={styles.label}>{t("externalportal.breeding.modal.sex")}</span>
                                                            <span className={styles.value}>
                                                                {dog.SEX === 'female' ? 'Tispe' : dog.SEX === 'male' ? 'Hannhund' : 'Ukjent'}
                                                            </span>
                                                        </div>
                                                        <div className={styles.dogDetail}>
                                                            <span className={styles.label}>{t("externalportal.breeding.modal.age")}</span>
                                                            <span className={styles.value}>
                                                                {dog.DATE_BIRTH ? 
                                                                    Math.floor((new Date().getTime() - new Date(dog.DATE_BIRTH).getTime()) / (1000 * 60 * 60 * 24 * 365.25)) + ' år' : 
                                                                    'Ukjent'
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className={styles.dogDetail}>
                                                            <span className={styles.label}>{t("externalportal.breeding.modal.dogtitle")}</span>
                                                            <span className={styles.value}>{dog.TITLE || 'Ukjent'}</span>
                                                        </div>
                                                        {/* <div className={styles.dogDetail}>
                                                            <span className={styles.label}>{t("externalportal.breeding.modal.owner")}</span>
                                                            <span className={styles.value}>{dog.OWNER_NAME || 'Ukjent'}</span>
                                                        </div>
                                                        <div className={styles.dogDetail}>
                                                            <span className={styles.label}>{t("externalportal.breeding.modal.location")}</span>
                                                            <span className={styles.value}>{dog.POSTAL_AREA || 'Ukjent'}</span>
                                                        </div> */}
                                                    </div>
                                                </div>
                                                <div className={styles.selectionIndicator}>
                                                    {selectedDog?.REGNO === dog.REGNO && <span className={styles.checkmark}>✓</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : !hasSearched ? (
                                <div className={styles.noResults}>
                                    <p>{t("externalportal.breeding.modal.searchabove")}</p>
                                </div>
                            ) : (
                                <div className={styles.noResults}>
                                    <p>{t("externalportal.breeding.modal.nofound")}</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}>
                {selectDog && hasSearched && (
                    <div className={styles.footerContent}>
                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    className={styles.paginationButton}
                                    onClick={() => setPage(Math.max(0, page - 1))}
                                    disabled={page === 0}
                                > 
                                    Forrige
                                </button>
                                <span className={styles.paginationInfo}>
                                    Side {page + 1} av {totalPages} (Totalt: {totalDogs} hunder)
                                </span>
                                <button
                                    className={styles.paginationButton}
                                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                                    disabled={page >= totalPages - 1}
                                >
                                    Neste
                                </button>
                            </div>
                        )}
                        <div className={styles.footerButtons}>
                            <Button 
                                variant="primary" 
                                onClick={handleConfirmSelection}
                                disabled={!selectedDog}
                            >
                                {t("externalportal.breeding.modal.select")}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default DogSelectionModal;
